// import { ObservableWrapperService } from 'idk/services/observable-wrapper.service';
import { ApiResult } from '../../data/api-result';
import { DataRepository } from 'idk/data/data-repository';
import { IUserProfile } from '../../models/security/user-profile';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SubscriptionHandler } from 'idk/data/subscription-handler';
import { ObservableWrapperResolverService } from '../observable-wrapper-resolver.service';

export interface IIdentityProfile {
    principal: IUserProfile;
    impersonated: IUserProfile[];
}

@Injectable({
    providedIn: 'root',
})
export class UserProfileService extends DataRepository {
    protected api: string = 'api/security/authentication';

    private _userProfile: IUserProfile = null;

    private _userProfileSubject: ReplaySubject<IUserProfile> =
        new ReplaySubject<IUserProfile>(1);

    private _role: string = null;

    public get role(): string {
        return (
            this._role ?? (this._role = localStorage.getItem('__user_role__'))
        );
    }
    public set role(value: string) {
        this._role = value;

        if (value == null) {
            localStorage.removeItem('__user_role__');
        } else {
            localStorage.setItem('__user_role__', value);
        }
    }

    public constructor(
        protected http: HttpClient,
        protected _router: Router,
        private owr: ObservableWrapperResolverService
    ) {
        super(null /*ow.wrapper*/);
    }

    public get hasMultipleRoles(): boolean {
        return this.user?.roles?.length > 1;
    }

    public set user(value: IUserProfile) {
        this._userProfile = value;
        this._userProfileSubject.next(value);
    }

    public get user(): IUserProfile {
        return this._userProfile;
    }

    public get user$(): Observable<IUserProfile> {
        if (this._userProfile == null) {
            this.http
                .get<ApiResult<IUserProfile>>(
                    this.buildApiCall(this.role != null ? `Profile/${this.role}` : 'Profile'),
                    this.buildOptions()
                )
                .pipe(tap((r: ApiResult<IUserProfile>) => this.process(r)))
                .subscribe({
                    next: (r) => {
                        if (r.valid && r.data != null) {
                            this._userProfileSubject.next(
                                (this._userProfile = r.data)
                            );
                        } else {
                            this._userProfileSubject.next(null);
                            this._router.navigate(['/sign-in']);
                        }
                    },
                    error: (r) => {
                        this._userProfileSubject.next(null);
                        this._router.navigate(['/sign-in']);
                    },
                });
        }
        return this._userProfileSubject.asObservable();
    }

    public get isAdmin(): boolean {
        for (let role of this.user.roles) {
            if (role == 'Administrador') {
                return true;
            }
        }
        return false;
    }

    public update(user: IUserProfile): Observable<any> {
        return this.http.patch<IUserProfile>('api/common/user', { user }).pipe(
            map((response) => {
                this._userProfileSubject.next(response);
            })
        );
    }

    public getPrincipalProfile(
        handler?: SubscriptionHandler<IIdentityProfile>
    ): Observable<ApiResult<IIdentityProfile>> {
        const observable: Observable<ApiResult<IIdentityProfile>> = this.http.get<
            ApiResult<IIdentityProfile>
        >(this.buildApiCall('profile/principal'), this.buildOptions());

        if (handler) {
            this.owr.wrapper.subscribe(observable, handler);
        }

        return observable;
    }

    public getMyImpersonatedProfiles(
        handler?: SubscriptionHandler<IUserProfile[]>
    ): Observable<ApiResult<IUserProfile[]>> {
        const observable: Observable<ApiResult<IUserProfile[]>> = this.http.get<
            ApiResult<IUserProfile[]>
        >(this.buildApiCall('profile/impersonated'), this.buildOptions());

        if (handler) {
            this.owr.wrapper.subscribe(observable, handler);
        }

        return observable;
    }
}
